<template>
  <div v-if="!hidden" class="sp-rating" part="rating" :style="glyphImages">
    <component :is="starsTag" v-bind="starsAttributes" class="sp-rating__stars" part="stars" :aria-label="starsLabel">
      <i v-for="(glyph, index) in stars" :key="index" class="star" :class="glyph.class" aria-hidden="true"></i>
    </component>

    <component :is="linkTag" v-if="!noScore" v-bind="linkAttributes" class="sp-rating__score" part="score">
      {{ score }}
    </component>

    <a
      v-if="infoUrl"
      class="sp-rating__info-icon"
      part="info-icon"
      :href="infoUrl"
      v-bind="targetAttributes"
      :aria-label="tAccessibleName('rating_info')"
    >
      <sp-icon name="help-circle-filled" aria-hidden="true"></sp-icon>
    </a>

    <span v-if="showCount" class="sp-rating__count" part="count">
      <strong>{{ ratingCount }}</strong> {{ t("rating_count_label", ratingCount) }}
    </span>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { t, tAccessibleName } from "#ce/shared/components-i18n";
import { toBoolean } from "#ce/utils/props";
import { props as ratingProps } from "./props.js";
import fullStarSource from "./assets/star.svg?raw";
import halfStarSource from "./assets/star-half.svg?raw";

function svgUrl(source) {
  return `url("data:image/svg+xml,${encodeURIComponent(source.trim())}")`;
}

const props = defineProps(ratingProps);

const noScore = computed(() => toBoolean(props.noScore));
const ratingCount = computed(() => Number(props.count) || 0);

const MAX_RATING = 5;
// The largest digit count `Number#toFixed` accepts.
const MAX_FRACTION_DIGITS = 100;

const rating = computed(() => {
  const value = Number(props.rating);

  return Number.isFinite(value) && value > 0 ? Math.min(value, MAX_RATING) : 0;
});

// No empty stars follow the score: a rating of three draws three stars.
const stars = computed(() => {
  const whole = Math.floor(rating.value);
  const glyphs = Array.from({ length: whole }, () => ({ class: "full-star" }));
  const remainder = rating.value - whole;

  if (remainder >= 0.75) {
    glyphs.push({ class: "full-star" });
  } else if (remainder >= 0.25) {
    glyphs.push({ class: "half-star" });
  }

  return glyphs;
});

const fractionDigits = computed(() => {
  const digits = Number(props.fractionDigits);

  const supported = Number.isInteger(digits) && digits >= 0 && digits <= MAX_FRACTION_DIGITS;

  return props.fractionDigits !== undefined && supported ? digits : undefined;
});

function formatScore(value) {
  return fractionDigits.value === undefined ? value : value.toFixed(fractionDigits.value);
}

const hidden = computed(() => toBoolean(props.hideunrated) && !rating.value);
const showCount = computed(() => toBoolean(props.showcount) && Boolean(rating.value) && ratingCount.value > 0);

// An empty no-rating wording falls through to zero.
const score = computed(() =>
  rating.value ? formatScore(rating.value) : (props.noRating ?? t("rating_not_rated")) || 0,
);

const starsLabel = computed(() =>
  rating.value ? tAccessibleName("rating_stars_label", { score: rating.value, max: MAX_RATING }) : score.value,
);

const glyphImages = {
  "--sp-rating-full-star-image": svgUrl(fullStarSource),
  "--sp-rating-half-star-image": svgUrl(halfStarSource),
};

const linkTag = computed(() => (props.url ? "a" : "span"));

const SAME_CONTEXT_TARGETS = ["_self", "_top", "_parent"];

const targetAttributes = computed(() => {
  if (!props.target) {
    return {};
  }

  if (SAME_CONTEXT_TARGETS.includes(props.target)) {
    return { target: props.target };
  }

  return { target: props.target, rel: "noopener" };
});

const linkAttributes = computed(() => (props.url ? { href: props.url, ...targetAttributes.value } : {}));

// An empty link is a zero-size tab stop, so the stars link only while there are stars to click.
const starsTag = computed(() => (stars.value.length ? linkTag.value : "span"));
const starsAttributes = computed(() => (stars.value.length ? linkAttributes.value : {}));
</script>

<style>
:host {
  --rating-icon-size: var(--sp-comp-rating-icon-size, var(--sp-sys-icon-size-xxx-small));
  --rating-star-image-size: var(--sp-comp-rating-star-image-size, contain);
  --rating-star-spacing: var(--sp-comp-rating-star-spacing, 1px);
  --rating-gap: var(--sp-comp-rating-gap, 0);
  --rating-justify-content: var(--sp-comp-rating-justify-content, center);
  --rating-justify-content-sm: var(--sp-comp-rating-justify-content-sm, var(--rating-justify-content));
  --rating-info-icon-size: var(--sp-comp-rating-info-icon-size, var(--sp-sys-icon-size-xxx-small));
  --rating-info-icon-color: var(--sp-comp-rating-info-icon-color, var(--sp-sys-color-primary));
  --rating-font-size: var(--sp-comp-rating-font-size, var(--sp-sys-typescale-body-small-font-size));
  --rating-font-size-sm: var(--sp-comp-rating-font-size-sm, var(--sp-ref-font-size-semi-medium));
  --rating-line-height: var(--sp-comp-rating-line-height, var(--sp-sys-typescale-body-small-line-height));
  --rating-vertical-spacing: var(--sp-comp-rating-vertical-spacing, var(--sp-ref-scale-8));
  --rating-horizontal-spacing: var(--sp-comp-rating-horizontal-spacing, var(--sp-ref-scale-4));
  --rating-horizontal-spacing-end: var(--sp-comp-rating-horizontal-spacing-end, var(--rating-horizontal-spacing));
  --rating-font-family: var(--sp-comp-rating-font-family, inherit);
  --rating-font-weight: var(--sp-comp-rating-font-weight, inherit);
  --rating-score-color: var(--sp-comp-rating-score-color, inherit);
  --rating-count-font-size: var(--sp-comp-rating-count-font-size, var(--sp-ref-font-size-semi-medium));
  --rating-count-font-weight: var(--sp-comp-rating-count-font-weight, var(--sp-sys-font-weight-bold));
  --rating-count-line-height: var(--sp-comp-rating-count-line-height, inherit);

  display: block;
}
</style>

<style scoped>
.sp-rating {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: var(--rating-justify-content);
  gap: var(--rating-gap);
  margin: var(--rating-vertical-spacing) 0;
}

a {
  text-decoration: none;
}

.sp-rating__stars {
  display: flex;
}

.star {
  background-position: center;
  background-repeat: no-repeat;
  background-size: var(--rating-star-image-size);
  display: inline-block;
  width: var(--rating-icon-size);
  height: var(--rating-icon-size);
  min-width: var(--rating-icon-size);
  min-height: var(--rating-icon-size);
  margin: 0 var(--rating-star-spacing);
}

.full-star {
  background-image: var(--sp-rating-full-star-image);
}

.half-star {
  background-image: var(--sp-rating-half-star-image);
}

.sp-rating__count {
  flex-basis: 100%;
  font-size: var(--rating-count-font-size);
  line-height: var(--rating-count-line-height);
  text-align: center;
}

.sp-rating__count strong {
  font-weight: var(--rating-count-font-weight);
}

.sp-rating__score {
  color: var(--rating-score-color);
  font-family: var(--rating-font-family);
  font-weight: var(--rating-font-weight);
  margin-block: 0;
  margin-inline: var(--rating-horizontal-spacing) var(--rating-horizontal-spacing-end);
  font-size: var(--rating-font-size);
  line-height: var(--rating-line-height);
  white-space: nowrap;
  height: 1.11em;
}

/* $breakpoint-sm from assets/stylesheets/mixins/_breakpoint.scss. */
@media (min-width: 751px) {
  .sp-rating {
    justify-content: var(--rating-justify-content-sm);
  }

  .sp-rating__score {
    font-size: var(--rating-font-size-sm);
  }
}

.sp-rating__info-icon {
  line-height: 0;
  width: var(--rating-info-icon-size);
  height: var(--rating-info-icon-size);
  min-width: var(--rating-info-icon-size);
  min-height: var(--rating-info-icon-size);
  vertical-align: middle;
}

.sp-rating__info-icon sp-icon {
  --sp-comp-icon-size: var(--rating-info-icon-size);
  --sp-comp-icon-fill: var(--rating-info-icon-color);

  display: block;
}
</style>
